import { useEffect, useState, FC, MouseEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { Link } from 'routes';
import classNames from 'classnames';

import { useWindowSize } from 'src/hooks';
import DropDownWrapper from 'components/atoms/DropDownWrapper';
import DayTimePillPopup, { DayTimePillPopupProps } from 'components/molecules/DayTimePillPopup';
import Pill from '@z-components/atoms/Pill';
import {
  MATCHING_CRITERIA_QUERY_PARAM_KEY,
  PAGE_QUERY_PARAM_KEY,
} from 'modules/marketing-pages/utils';
import {
  PillType,
  DropdownListItem,
  DropdownItemTypes,
  ScrollerItemData,
  PillFiltersProps,
} from './PillFilters.types';
import { FILTER_TYPES, STATIC_FILTER_IDS } from 'modules/marketing-pages/duck';
import { checkIsDesktop } from 'utils/layout';

import type { RootState } from 'store/types';
import styles from './PillFilters.module.scss';

const PillParent: FC<{ link?: string }> = ({ children, link }) => {
  if (!link) {
    return <>{children}</>;
  }
  return (
    <Link prefetch={false} route={link} shallow={true} scroll={false} passHref={true}>
      <a className={styles['pill-anchor']} tabIndex={0} role="button">
        {children}
      </a>
    </Link>
  );
};

const PillRow: FC<{ onScroll: () => void }> = ({ children, onScroll }) => (
  <div className={styles['scrolling-row']} onScroll={() => onScroll()}>
    {children}
  </div>
);

const groupPillsByLine = (pills: Array<ScrollerItemData>, isDesktopOrTablet: boolean) => {
  const maxPillsBeforeSplitOnMobile = 8;
  const shouldBeMultiRow = !isDesktopOrTablet && pills.length > maxPillsBeforeSplitOnMobile;
  const step = shouldBeMultiRow ? Math.round(pills.length / 2) : pills.length;
  const pillsByRow = [];
  let pillRow: Array<ScrollerItemData> = [];
  for (let i = 0; i < pills.length; i++) {
    if (i % step === 0 && i > 0) {
      pillsByRow.push(pillRow);
      pillRow = [];
    }
    pillRow.push(pills[i]);
  }
  if (pillRow.length > 0) {
    pillsByRow.push(pillRow);
  }
  return pillsByRow;
};

const PillFilters: FC<PillFiltersProps> = ({
  jobType,
  pillFiltersData = [],
  onPillFilterClick,
  onPillFilterChange,
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { asPath } = router;
  const [openedPillId, setOpenedPillId] = useState('');
  // eslint-disable-next-line id-length
  const [position, setPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isDayTimePillPopupShown, setIsDayTimePillPopupShown] = useState(false);
  const { width: windowWidth } = useWindowSize();
  const isDesktop = useMemo(() => checkIsDesktop(windowWidth), [windowWidth]);

  const [selectedItemDropdownList, setSelectedItemDropdownList] = useState<Array<DropdownListItem>>(
    []
  );

  const [selectedElement, setSelectedElement] = useState<Element | null>(null);

  const selectedFiltersAndValues = useSelector(
    (state) => (state as RootState).marketingPages.filters
  );

  const idToScrollerItemDataMap: { [key: string]: ScrollerItemData } = Object.fromEntries(
    pillFiltersData.map((pillerFilter) => [pillerFilter.id, pillerFilter])
  );

  const updateSelectedPillLocation = useCallback(() => {
    if (selectedElement) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, id-length
      const { x, y }: { x: number; y: number } = selectedElement.getBoundingClientRect();
      // eslint-disable-next-line id-length
      setPosition({ x, y });
    }
  }, [selectedElement]);

  useEffect(() => {
    updateSelectedPillLocation();
  }, [selectedElement, updateSelectedPillLocation]);

  const handlePillClicked = (event: MouseEvent<HTMLDivElement, MouseEvent>, id: string) => {
    setSelectedElement(event.target as Element);
    setOpenedPillId(id);
    const pillData = pillFiltersData.find((data) => data.id === id);
    if (!pillData) return;
    const { type } = pillData;

    switch (type) {
      case PillType.DayTime:
        setIsDayTimePillPopupShown(true);
        setSelectedItemDropdownList([]);
        break;
      case PillType.Dropdown:
        setDropdownMenuVisibility(true);
        setSelectedItemDropdownList(pillData.dropdownList ?? []);
        break;
      case PillType.Toggle:
        setDropdownMenuVisibility(false);
        setSelectedItemDropdownList([]);
        handlePillToggle(pillData.id);
    }
  };

  const handlePillToggle = (id: string) => {
    const scrollerItemData = idToScrollerItemDataMap[id];
    const isToggled = Boolean(
      selectedFiltersAndValues['matchingCriteria'][scrollerItemData.key]?.includes(
        scrollerItemData.value
      )
    );

    onPillFilterChange({
      filterId: `${scrollerItemData.key}.${scrollerItemData.value}`,
      filterType: FILTER_TYPES.matchingCriteria,
      filterValue: isToggled ? [] : [scrollerItemData.value],
    });

    // Add check against matching criteria type of filter
    // IF it is matching criteria filter, then execute matching criteria related action then return the function execution
    if (scrollerItemData.onToggleValueChanged) {
      scrollerItemData.isToggleSelected = !isToggled;
      scrollerItemData.onToggleValueChanged(scrollerItemData);
      return;
    }

    onPillFilterClick(id, isToggled ? [''] : [id]);
  };

  const dropDownItemSelected = (dropdownMenuItem: DropdownListItem) => {
    const isRemovingSelectedOption = dropdownMenuItem.id === DropdownItemTypes.Unselect;

    onPillFilterChange({
      filterId: dropdownMenuItem.key,
      filterType: FILTER_TYPES.priceRange,
      filterValue: isRemovingSelectedOption ? [] : [dropdownMenuItem.value],
    });

    setDropdownMenuVisibility(false);

    if (dropdownMenuItem.onListValueSelected) {
      dropdownMenuItem.onListValueSelected(dropdownMenuItem.id);
    }
  };

  const handleDayTimeSubmit = (values: Parameters<DayTimePillPopupProps['onSubmit']>[0]) => {
    onPillFilterChange({
      filterId: STATIC_FILTER_IDS.DateTime,
      filterType: FILTER_TYPES.DateTime,
      filterValue: values,
    });
    onPillFilterClick(openedPillId, values);
  };

  useEffect(() => {
    setOpenedPillId('');
    setIsDayTimePillPopupShown(false);
  }, [selectedFiltersAndValues]);

  const isDayAndTimeFilterEmpty = (day: string, time: string) => day === '' && time === '';
  const isPillValueSelected = (id: string) => {
    const pillData = pillFiltersData.find((data) => data.id === id);
    if (!pillData) return false;
    if (pillData.isToggleSelected) return true;
    const { type } = pillData;
    if (type === PillType.DayTime) {
      const { day = '', time = '' } = selectedFiltersAndValues;
      return !isDayAndTimeFilterEmpty(day, time);
    }
    return id in selectedFiltersAndValues;
  };

  const getSelectedPillFiltersText = (id: string) => {
    const pillData = pillFiltersData.find((data) => data.id === id);
    if (!pillData) return;
    const { type } = pillData;

    if (type === PillType.DayTime) {
      const { day = '', time = '' } = selectedFiltersAndValues;
      if (isDayAndTimeFilterEmpty(day, time)) return pillData.pillText;
      const dayDisplay = day ? t(`common:daysOfTheWeek.${day}`) : day;
      const timeOfDayDisplay = time ? t(`common:timesOfDay.${time}`) : time;

      return `${dayDisplay} ${timeOfDayDisplay}`;
    }
    if (type === PillType.Dropdown && id === 'priceRange') {
      return selectedFiltersAndValues['priceRange']?.[0] ?? pillData.pillText;
    }
    return pillData.pillText;
  };

  const [dropdownMenuVisibility, setDropdownMenuVisibility] = useState<boolean>(false);

  // Extend in future
  const getPillLink = (data: ScrollerItemData) => {
    switch (data.type) {
      case PillType.DayTime:
        return '';
      case PillType.Dropdown:
        return '';
      case PillType.Toggle:
        return getMatchingCriteriaLink(data);
      default:
        return '';
    }
  };

  const getMatchingCriteriaLink = useCallback(
    (data: ScrollerItemData) => {
      const { searchParams, pathname } = new URL(
        asPath,
        process.env.DOMAIN ?? 'https://zehitomo.com'
      );
      if (searchParams.has(PAGE_QUERY_PARAM_KEY)) {
        searchParams.delete(PAGE_QUERY_PARAM_KEY);
      }
      const matchingCriteriaFieldKeyAndOptionValue = `${data.matchingCriteriaKey ?? ''}.${
        data.matchingCriteriaOptionValue ?? ''
      }`;
      const matchingCriteriaQueryString: string =
        searchParams.get(MATCHING_CRITERIA_QUERY_PARAM_KEY)?.toString() ?? '';
      const parsedMatchingCriteriaQueryString =
        matchingCriteriaQueryString.length > 0 ? matchingCriteriaQueryString.split(',') : [];
      const isValueInMatchingCriteriaQuery = parsedMatchingCriteriaQueryString.includes(
        matchingCriteriaFieldKeyAndOptionValue
      );

      if (isValueInMatchingCriteriaQuery && data.isToggleSelected) {
        const removalIndex = parsedMatchingCriteriaQueryString.indexOf(
          matchingCriteriaFieldKeyAndOptionValue
        );
        parsedMatchingCriteriaQueryString.splice(removalIndex, 1);
        searchParams.set(
          MATCHING_CRITERIA_QUERY_PARAM_KEY,
          parsedMatchingCriteriaQueryString.join(',')
        );
        if (searchParams.get(MATCHING_CRITERIA_QUERY_PARAM_KEY)?.length === 0) {
          searchParams.delete(MATCHING_CRITERIA_QUERY_PARAM_KEY);
        }
        return `${pathname}${searchParams.toString() && '?'}${searchParams.toString()}`;
      }
      const joinedParams = `${matchingCriteriaQueryString}${
        matchingCriteriaQueryString && ','
      }${matchingCriteriaFieldKeyAndOptionValue}`;
      searchParams.set(MATCHING_CRITERIA_QUERY_PARAM_KEY, joinedParams);

      const link = `${pathname}${searchParams.toString() && '?'}${searchParams.toString()}`;
      return link;
    },
    [asPath]
  );

  const pillRows = useMemo(
    () => groupPillsByLine(pillFiltersData, isDesktop),
    [pillFiltersData, isDesktop]
  );

  return (
    <>
      <div className={styles['scrolling-wrapper']}>
        {pillRows.map((pillRow, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <PillRow key={index} onScroll={() => updateSelectedPillLocation()}>
            {pillRow.map((pillData) => (
              <div className={classNames(styles['scroll-item'])} key={pillData.id}>
                <PillParent link={pillData.link ? getPillLink(pillData) : undefined}>
                  {pillData.type === PillType.Dropdown ? (
                    <DropDownWrapper
                      dropDownItemSelected={dropDownItemSelected}
                      dropdownList={selectedItemDropdownList}
                      isMenuOpen={dropdownMenuVisibility}
                      // eslint-disable-next-line react/jsx-no-bind
                      onDismiss={() => setDropdownMenuVisibility(false)}
                      position={position}
                    >
                      <Pill
                        text={
                          isPillValueSelected(pillData.id)
                            ? getSelectedPillFiltersText(pillData.id)
                            : pillData.pillText
                        }
                        icon={pillData.pillIcon}
                        id={pillData.id}
                        selected={isPillValueSelected(pillData.id)}
                        iconPosition={pillData.iconPosition}
                        // eslint-disable-next-line react/jsx-no-bind
                        onClick={handlePillClicked as () => void}
                      />
                    </DropDownWrapper>
                  ) : (
                    <Pill
                      text={
                        isPillValueSelected(pillData.id)
                          ? getSelectedPillFiltersText(pillData.id)
                          : pillData.pillText
                      }
                      icon={pillData.pillIcon}
                      id={pillData.id}
                      selected={isPillValueSelected(pillData.id)}
                      iconPosition={pillData.iconPosition}
                      // eslint-disable-next-line react/jsx-no-bind
                      onClick={handlePillClicked as () => void}
                    />
                  )}
                </PillParent>
              </div>
            ))}
          </PillRow>
        ))}
      </div>
      {isDayTimePillPopupShown && (
        <DayTimePillPopup
          onDismiss={setIsDayTimePillPopupShown}
          onSubmit={handleDayTimeSubmit}
          showDay={jobType.filterSettings?.day}
          showTime={jobType.filterSettings?.time}
        />
      )}
    </>
  );
};

export default PillFilters;
